import React, { useContext, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'

import { makeStyles } from '@material-ui/core/styles'
import { Typography, Select, FormControl, MenuItem, ListItemIcon, Box } from '@material-ui/core'
import { ExpandMore } from '@material-ui/icons'

import api from '~/api'
import actions from '~/actions'
import { getUrlParams } from '~/utils'

import { useLiterals } from '~/components/Literals'
import { selectChart } from '~/selectors'

import { trackApi, OnlineStoreContext } from '.'

const useStyles = makeStyles((theme) => ({
  select: {
    backgroundColor: '#FFF',

    '& > .MuiSelect-root': {
      display: 'flex',
      alignItems: 'center',
      fontSize: '1rem',
    },
  },
  icon: {
    fill: theme.palette.primary.main,
    marginTop: -1,
    fontSize: 28,
  },
  productImg: {
    maxWidth: '100%',
    height: 30,
  },
  paper: {
    maxHeight: 300,
    ...theme.gofind.scrollBar,
  },
}))

export function FormSelectProduct() {
  const classes = useStyles()
  const history = useHistory()

  const { getLiteral } = useLiterals()
  const { config, ecommerce, position, gtin, products, isEnableAlertPrice, priceMonitoring, dispatch } =
    useContext(OnlineStoreContext)

  // Dispatch to update vars only chart property in redux
  const dispatchChart = useDispatch()

  const [chart] = useSelector(selectChart, shallowEqual)
  const [loadingGtin, setLoadingGtin] = useState(false)
  const initialLoad = useRef(true)

  const { period } = chart

  async function handleRequestProduct({ gtin }) {
    const brandName = config?.brandName
    const userLocation = position?.user
    const currentProduct = products.find((p) => p.gtin === gtin)

    // Define request origin API
    ecommerce.setApiEcommerce({ name: 'ecommerceGoogle', location: userLocation?.city })

    // Update state with current product
    dispatch({ gtin, currentProduct })

    // Dispatch track event
    trackApi({ event: 'ecommerce', brandName }, currentProduct, null, config, position)

    if (currentProduct) {
      ecommerce.setOpenEcommerce(currentProduct)

      // Save access log only with exiting product
      const { lat, lng: lon } = userLocation || {}
      await api.saveLogger({
        lat,
        lon,
        cnpj: config?.cnpj,
        gtin,
      })

      return
    }

    ecommerce.setOpenEcommerce('all')
  }

  function handleChangeProduct(event) {
    if (location.search) {
      history.replace({ search: `?gtin=${event.target.value}` })
      return
    }

    handleRequestProduct({ gtin: event.target.value })
  }

  // Save log of type:ecommerce when accessing the screen with query string
  useEffect(() => {
    if (!location.search) {
      setLoadingGtin(false)
      return
    }

    const query = getUrlParams(location.search)
    if (!query.gtin || query.gtin === 'none') {
      setLoadingGtin(false)
      return
    }

    if (initialLoad.current) {
      setLoadingGtin(true)
      initialLoad.current = false
    }

    if (products && products.length > 0) {
      const found = products.find((p) => p.gtin === query.gtin)
      if (found && gtin !== query.gtin) {
        dispatch({ gtin: query.gtin, currentProduct: found, isPendingGtinInProducts: false })
        setLoadingGtin(false)

        handleRequestProduct({ gtin: query.gtin })
      } else if (!found) {
        setLoadingGtin(true)
      }
    }
  }, [location.search, products])

  useEffect(() => {
    async function getEcommerceHistory() {
      if (!priceMonitoring.length) {
        dispatchChart(actions.chart.data([]))
        return
      }

      if (!ecommerce.openEcommerce?.gtin) {
        dispatchChart(actions.chart.data([]))
        return
      }

      const isProductMonitored = priceMonitoring.some((pm) => pm.gtin === ecommerce.openEcommerce.gtin)
      if (!isProductMonitored) {
        dispatchChart(actions.chart.data([]))
        return
      }

      dispatchChart(actions.chart.load(true))
      try {
        const insights = await api.ecommerceHistoryInsights(ecommerce.openEcommerce.gtin, period)
        if (Array.isArray(insights)) {
          dispatchChart(actions.chart.data(insights))
        } else {
          dispatchChart(actions.chart.data([]))
        }
      } catch (err) {
        console.error('Erro ao buscar histórico de ecommerce:', err)
        dispatchChart(actions.chart.data([]))
      } finally {
        dispatchChart(actions.chart.load(false))
      }
    }

    getEcommerceHistory()
  }, [ecommerce.openEcommerce, period, isEnableAlertPrice, priceMonitoring])

  return (
    <Box display="flex" justifyContent="center" width="100%" bgcolor="#EEEBEB">
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        padding={2}
        width="100%"
        maxWidth={700}
        bgcolor="#EEEBEB"
      >
        <Typography variant="body1" style={{ fontSize: 16, marginBottom: 8 }}>
          {getLiteral('1357')}:
        </Typography>
        <FormControl variant="outlined">
          <Select
            autoFocus
            value={gtin}
            onChange={handleChangeProduct}
            className={classes.select}
            inputProps={{ classes: { icon: classes.icon } }}
            IconComponent={(props) => <ExpandMore {...props} />}
            MenuProps={{
              classes: { paper: classes.paper },
              anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
              transformOrigin: { vertical: 'top', horizontal: 'center' },
              getContentAnchorEl: null,
            }}
            disabled={loadingGtin}
          >
            <MenuItem value="none" disabled>
              <Typography variant="body1" style={{ fontWeight: 500 }}>
                {loadingGtin ? getLiteral('1503') + '...' : getLiteral('1778') + '...'}
              </Typography>
            </MenuItem>

            {!loadingGtin &&
              products.map((product) => (
                <MenuItem key={product.gtin} value={product.gtin}>
                  <ListItemIcon>
                    <img
                      src={product.imageurl}
                      alt={product.name}
                      loading="lazy"
                      className={classes.productImg}
                      onError={({ currentTarget }) => {
                        currentTarget.onerror = null // prevents looping
                        currentTarget.src = `https://s3.sa-east-1.amazonaws.com/gofind.img.repo/productfinder/Grupo+417.svg`
                      }}
                    />
                  </ListItemIcon>
                  <Typography variant="inherit">{product.name}</Typography>
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Box>
    </Box>
  )
}
